import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Users as UsersIcon, Eye, Plus, Pencil, Trash2, RefreshCw } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

// ── Constants ─────────────────────────────────────────────────────────────────
const ROLES = [
  { key: "super_admin",        label: "Super Admin",      desc: "Platform owner — full access" },
  { key: "platform_support",   label: "Platform Support", desc: "Platform support staff" },
  { key: "customer_admin",     label: "Org Admin",        desc: "Full org administration" },
  { key: "admin",              label: "Admin",            desc: "Org-scoped admin" },
  { key: "site_manager",       label: "Site Manager",     desc: "Manage sites, panels, devices" },
  { key: "building_manager",   label: "Bldg Manager",     desc: "Operational building oversight" },
  { key: "fire_safety_officer",label: "Safety Officer",   desc: "Monitoring + alert acknowledge" },
  { key: "viewer",             label: "Viewer",           desc: "Read-only dashboard access" },
  { key: "sales",              label: "Sales",            desc: "View + reports access" },
];

const MODULE_GROUPS = [
  {
    group: "Monitoring",
    modules: [
      { key: "hydrant_system",    label: "Hydrant System" },
      { key: "sprinkler_system",  label: "Sprinkler System" },
      { key: "fire_water_tank",   label: "Fire Water Tank" },
      { key: "detection_system",  label: "Detection System" },
      { key: "ventilation_system",label: "Ventilation System" },
      { key: "lifts_module",      label: "Lifts Module" },
      { key: "addressable_panel", label: "Addressable Panel" },
    ],
  },
  {
    group: "BMS Monitoring",
    modules: [
      { key: "iaq_monitor",       label: "IAQ Monitor" },
      { key: "energy_dashboard",  label: "Energy Dashboard" },
      { key: "maintenance_scheduler", label: "Maintenance Scheduler" },
      { key: "work_orders",       label: "Work Orders" },
    ],
  },
  {
    group: "Asset Registries",
    modules: [
      { key: "pumps_registry",      label: "Pumps Registry" },
      { key: "pressure_sensors",    label: "Pressure Sensors" },
      { key: "tanks_registry",      label: "Tanks Registry" },
      { key: "valves_registry",     label: "Valves Registry" },
      { key: "lifts_registry",      label: "Lifts Registry" },
      { key: "fans_ahu_registry",   label: "Fans & AHU" },
      { key: "fire_alarm_devices",  label: "Fire Alarm Devices" },
    ],
  },
  {
    group: "Management",
    modules: [
      { key: "panels_management",   label: "Panels Management" },
      { key: "device_management",   label: "Device Management" },
      { key: "site_management",     label: "Site Management" },
      { key: "floor_plans",         label: "Floor Plans" },
      { key: "hardware_gateways",   label: "Hardware Gateways" },
      { key: "gateway_status",      label: "Gateway Status" },
      { key: "gateway_commands",    label: "Gateway Commands" },
    ],
  },
  {
    group: "Operations",
    modules: [
      { key: "alert_management",    label: "Alert Management" },
      { key: "reports_logs",        label: "Reports & Logs" },
      { key: "audit_log",           label: "Audit Log" },
    ],
  },
  {
    group: "Administration",
    modules: [
      { key: "user_management",     label: "User Management" },
      { key: "roles_permissions",   label: "Roles & Permissions" },
      { key: "acms_certificate",    label: "ACMS Certificate" },
      { key: "controller_settings", label: "Controller Settings" },
      { key: "system_settings",     label: "System Settings" },
      { key: "billing_plan",        label: "Billing & Plan" },
      { key: "subscription_revenue",label: "Subscription Revenue" },
    ],
  },
];

const ACTIONS = [
  { key: "can_view",   label: "View",   icon: Eye,    color: "text-sky-400" },
  { key: "can_create", label: "Create", icon: Plus,   color: "text-emerald-400" },
  { key: "can_update", label: "Update", icon: Pencil, color: "text-amber-400" },
  { key: "can_delete", label: "Delete", icon: Trash2, color: "text-red-400" },
];

// ── Toggle chip ───────────────────────────────────────────────────────────────
function ToggleChip({ value, onChange, readonly, actionKey }) {
  const action = ACTIONS.find((a) => a.key === actionKey);
  const Icon = action?.icon;

  if (readonly) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
        value
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-gray-50 text-gray-500 border-gray-200"
      }`}>
        {Icon && <Icon className="w-3 h-3" />}
        {value ? action?.label : "—"}
      </span>
    );
  }

  return (
    <button
      onClick={() => onChange(!value)}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border transition-all hover:scale-105 ${
        value
          ? `bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100`
          : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100 hover:text-gray-600"
      }`}
    >
      {Icon && <Icon className="w-3 h-3" />}
      {value ? action?.label : "—"}
    </button>
  );
}

// ── Module row ────────────────────────────────────────────────────────────────
function ModuleRow({ moduleKey, moduleLabel, roleKey, perms, isSuperAdmin, onToggle, saving }) {
  const cellPerms = perms?.[roleKey]?.[moduleKey] || {};

  return (
    <tr className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-2.5 text-sm font-medium text-gray-700">{moduleLabel}</td>
      {ACTIONS.map((action) => (
        <td key={action.key} className="px-3 py-2.5 text-center">
          <ToggleChip
            value={cellPerms[action.key] ?? false}
            actionKey={action.key}
            readonly={!isSuperAdmin || saving}
            onChange={(val) => onToggle(roleKey, moduleKey, action.key, val)}
          />
        </td>
      ))}
    </tr>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RolesPermissions() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedRole, setSelectedRole] = useState("site_manager");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/roles/permissions")
      .then((r) => setPerms(r.data || {}))
      .catch(() => toast.error("Failed to load permissions"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleToggle = async (role, moduleKey, actionKey, value) => {
    if (!isSuperAdmin) return;
    // Optimistic update
    setPerms((prev) => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [moduleKey]: {
          ...(prev[role]?.[moduleKey] || {}),
          [actionKey]: value,
        },
      },
    }));
    setSaving(true);
    try {
      await api.put(`/roles/permissions/${role}/${moduleKey}`, { [actionKey]: value });
    } catch (e) {
      toast.error("Failed to save permission");
      load(); // revert on failure
    } finally {
      setSaving(false);
    }
  };

  const roleObj = ROLES.find((r) => r.key === selectedRole);
  const isCurrentRole = user?.role === selectedRole;

  return (
    <div className="space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Administration → Roles & Permissions</div>
          <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-orange-400" strokeWidth={2.5} /> Roles & Permissions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Module-wise Create / Update / View / Delete permissions per role.
            {isSuperAdmin ? " Click any chip to toggle." : " Read-only — super_admin can edit."}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={load} disabled={loading} className="gap-1 text-xs h-9">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Link to="/users">
            <Button className="f360-gradient border-0 gap-1" data-testid="go-users-btn">
              <UsersIcon className="w-4 h-4" /> Manage Users
            </Button>
          </Link>
        </div>
      </div>

      {/* Role selector tabs */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map((r) => (
          <button
            key={r.key}
            onClick={() => setSelectedRole(r.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
              selectedRole === r.key
                ? "f360-gradient border-0 text-white"
                : "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
            }`}
          >
            {r.label}
            {user?.role === r.key && (
              <span className="ml-1.5 text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-bold">YOU</span>
            )}
          </button>
        ))}
      </div>

      {/* Role description */}
      {roleObj && (
        <div className="glass rounded-lg px-4 py-3 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-orange-400 shrink-0" />
          <div>
            <span className="font-semibold">{roleObj.label}</span>
            <span className="text-gray-500 text-sm ml-2">— {roleObj.desc}</span>
          </div>
          {(roleObj.key === "super_admin" || roleObj.key === "platform_support" ||
            roleObj.key === "customer_admin" || roleObj.key === "admin") && (
            <Badge className="ml-auto bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">
              Full Access (all modules)
            </Badge>
          )}
        </div>
      )}

      {/* Permission matrix */}
      {loading ? (
        <div className="text-gray-500 text-sm py-8 text-center">Loading permissions…</div>
      ) : (
        <div className="space-y-4">
          {MODULE_GROUPS.map((group) => (
            <div key={group.group} className="glass rounded-lg overflow-hidden">
              <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <span className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">
                  {group.group}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-gray-600 border-b border-gray-200">
                      <th className="text-left px-4 py-2 min-w-[180px]">Module</th>
                      {ACTIONS.map((a) => (
                        <th key={a.key} className="px-3 py-2 text-center">
                          <span className={`flex items-center justify-center gap-1 ${a.color}`}>
                            <a.icon className="w-3 h-3" /> {a.label}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {group.modules.map((m) => (
                      <ModuleRow
                        key={m.key}
                        moduleKey={m.key}
                        moduleLabel={m.label}
                        roleKey={selectedRole}
                        perms={perms}
                        isSuperAdmin={isSuperAdmin}
                        onToggle={handleToggle}
                        saving={saving}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isSuperAdmin && (
        <div className="glass rounded-lg p-4 text-xs text-gray-500">
          <span className="text-orange-700 font-bold uppercase tracking-wider text-[10px] mr-2">Note</span>
          Only super_admin can modify role permissions. Contact your platform administrator to request changes.
        </div>
      )}
    </div>
  );
}
